<template>
  <div class="min-h-screen bg-gray-50">
    <div id="dropdown" />
    
    <div class="flex h-screen overflow-hidden">
      <!-- Collapsible Sidebar -->
      <main-menu :auth="auth" :is-mobile-open="isMobileMenuOpen" />

      <!-- Main Content -->
      <div class="flex flex-col flex-1 overflow-hidden">
        <!-- Top Navigation -->
        <div class="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button 
            class="md:hidden px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            @click="toggleSidebar"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <div class="flex-1 px-4 flex justify-between">
            <div class="flex-1 flex items-center">
              <!-- Exchange Rate -->
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>1$ = {{ formatCurrency(change) }} BsD</span>
                <Link
                  href="/taxes"
                  class="px-2 py-0.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-700 rounded border border-indigo-200 transition-colors shadow-sm"
                >
                  Modificar tasa
                </Link>
              </div>
            </div>

            <div class="flex items-center space-x-4">
              <!-- Time Display -->
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="font-medium">{{ time }}</span>
              </div>

              <!-- User Menu -->
              <dropdown placement="bottom-end">
                <template #default>
                  <button class="flex items-center space-x-3 focus:outline-none">
                    <div class="flex items-center space-x-2">
                      <div class="text-sm font-medium text-gray-700">
                        {{ auth.user.first_name }}
                        <span class="hidden md:inline">{{ auth.user.last_name }}</span>
                      </div>
                      <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                </template>
                <template #dropdown>
                  <div class="mt-2 py-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                    <Link 
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" 
                      :href="`/users/${auth.user.id}/edit`"
                    >
                      Mi perfil
                    </Link>
                    <Link 
                      v-if="auth.user.role === 'admin'" 
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" 
                      href="/users"
                    >
                      Usuarios
                    </Link>
                    <Link 
                      v-if="auth.user.role === 'admin'" 
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" 
                      :href="`/accounts/${auth.user?.account?.id}`"
                    >
                      Cuenta
                    </Link>
                    <Link 
                      v-if="auth.user.role === 'admin'" 
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" 
                      href="/tools"
                    >
                      Herramientas
                    </Link>
                    <div class="border-t border-gray-100"></div>
                    <Link 
                      class="block px-4 py-2 text-sm text-red-600 hover:bg-gray-100 hover:text-red-700" 
                      href="/logout" 
                      method="delete" 
                      as="button"
                    >
                      Cerrar sesión
                    </Link>
                  </div>
                </template>
              </dropdown>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <main class="flex-1 relative overflow-y-auto focus:outline-none">
          <div class="py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <flash-messages />
              <slot />
            </div>
          </div>
        </main>
      </div>
    </div>

    <!-- Mobile Sidebar Backdrop Overlay -->
    <div 
      v-if="isMobileMenuOpen" 
      class="fixed inset-0 z-40 bg-black/40 md:hidden"
      @click="toggleSidebar"
    ></div>

    <bottom-menu />
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Logo from '@/Shared/Logo'
import Dropdown from '@/Shared/Dropdown'
import MainMenu from '@/Shared/MainMenu'
import FlashMessages from '@/Shared/FlashMessages'
import BottomMenu from './BottomMenu.vue'

export default {
  components: {
    Dropdown,
    FlashMessages,
    Icon,
    Link,
    Logo,
    MainMenu,
    BottomMenu
  },
  props: {
    auth: Object,
    change: Number
  },
  data() {
    return {
      time: '',
      isMobileMenuOpen: false,
      lastActivityTime: Date.now(),
      inactivityTimeoutMs: 60 * 60 * 1000, // 1 hora de inactividad
      inactivityCheckInterval: null,
      activityEvents: ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click']
    }
  },
  mounted() {
    this.updateTime()
    this.timeInterval = setInterval(this.updateTime, 1000)
    this.setupInactivityDetector()
  },
  beforeUnmount() {
    if (this.timeInterval) clearInterval(this.timeInterval)
    this.cleanupInactivityDetector()
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    updateTime() {
      const now = new Date()
      this.time = now.toLocaleTimeString('es-VE', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    },
    toggleSidebar() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    },
    recordActivity() {
      this.lastActivityTime = Date.now()
    },
    checkInactivity() {
      const idleTime = Date.now() - this.lastActivityTime
      if (idleTime >= this.inactivityTimeoutMs) {
        this.cleanupInactivityDetector()
        this.$inertia.delete('/logout', {
          onFinish: () => {
            window.location.href = '/login'
          }
        })
      }
    },
    setupInactivityDetector() {
      this.recordActivity()
      this.activityEvents.forEach(event => {
        window.addEventListener(event, this.recordActivity, { passive: true })
      })
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkInactivity()
        }
      })
      this.inactivityCheckInterval = setInterval(this.checkInactivity, 30000)
    },
    cleanupInactivityDetector() {
      if (this.inactivityCheckInterval) {
        clearInterval(this.inactivityCheckInterval)
        this.inactivityCheckInterval = null
      }
      this.activityEvents.forEach(event => {
        window.removeEventListener(event, this.recordActivity)
      })
    }
  }
}
</script>

<style scoped>
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>
